/**
 * Hook for updating document cover image
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { erp } from "@/lib/erp";

export function useUpdateCoverImage(doctype: string, name: string) {
  const queryClient = useQueryClient();

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      return erp.docs.uploadAttachment({ doctype, name, file });
    },
  });

  const setImageMutation = useMutation({
    mutationFn: async (fileUrl: string) => {
      return erp.docs.setCoverImage(doctype, name, fileUrl);
    },
    onSuccess: () => {
      // Invalidate sidebar to refresh image
      queryClient.invalidateQueries({
        queryKey: ["doc-sidebar", doctype, name],
      });
      // Also invalidate the main document query (e.g., item detail) to sync image
      // Note: query key pattern varies by doctype - invalidate all item queries
      if (doctype === "Item") {
        queryClient.invalidateQueries({
          queryKey: ["item", name],
        });
      } else {
        // Generic pattern for other doctypes
        queryClient.invalidateQueries({
          queryKey: [doctype.toLowerCase(), name],
        });
      }
      // Invalidate any list queries that might show the image
      queryClient.invalidateQueries({
        queryKey: ["list", doctype],
      });
    },
  });

  const updateCoverImage = async (file: File) => {
    // Step 1: Upload file
    const uploadedFile = await uploadMutation.mutateAsync(file);
    
    // Step 2: Set as cover image
    await setImageMutation.mutateAsync(uploadedFile.file_url);
  };

  return {
    updateCoverImage,
    isUploading: uploadMutation.isPending,
    isSetting: setImageMutation.isPending,
    isLoading: uploadMutation.isPending || setImageMutation.isPending,
    error: uploadMutation.error || setImageMutation.error,
  };
}

